import { FinancialHealthStats } from './types/financialStats';

interface Factor {
  key: keyof FinancialHealthStats;
  label: string;
  description: string;
}

export const FACTORS: Factor[] = [
  {
    key: 'verascore',
    label: 'VeraScore',
    description: 'Overall financial health score combining all factors',
  },
  {
    key: 'factor_dti',
    label: 'Debt-to-Income',
    description: 'Ratio of monthly debt payments to income',
  },
  // BUG-UI-040 FIX: Renamed from "Cash on Hand" to "Liquid Reserves" for consistency
    // This factor measures months of expenses covered by liquid assets, not just cash balance
  {
    key: 'factor_cash_on_hand',
    label: 'Liquid Reserves',
    description: 'Months of expenses covered by liquid assets',
  },
  {
    key: 'factor_spending',
    label: 'Spending',
    description: 'Monthly spending patterns and budget management',
  },
  {
    key: 'factor_savings',
    label: 'Savings',
    description: 'Net savings relative to income',
  },
  {
    key: 'factor_debt',
    label: 'Debt',
    description: 'Outstanding debt and payment behavior',
  },
  {
    key: 'factor_payment_history',
    label: 'Payment History',
    description: 'Track record of on-time payments',
  },
];

interface FinancialMetric {
  key: keyof FinancialHealthStats | 'credit_score';
  label: string;
  format: 'percentage' | 'currency' | 'number';
  description: string;
  chartType?: 'area' | 'bar' | 'composed';
  referenceLines?: {
    y: number;
    label: string;
    color: string;
  }[];
}

export const FINANCIAL_METRICS: FinancialMetric[] = [
  {
    key: 'gross_income',
    label: 'Gross Income',
    format: 'currency',
    description: 'Monthly gross income before taxes and deductions',
    chartType: 'area',
  },
  {
    key: 'minimum_debt_payments',
    label: 'Minimum debt payments',
    format: 'currency',
    description: 'Total required monthly debt payments',
    chartType: 'area',
  },
  {
    key: 'liquid_reserves',
    label: 'Actual Cash on Hand',
    format: 'currency',
    description: 'Liquid assets available for emergencies',
    chartType: 'area',
  },
  {
    key: 'money_in',
    label: 'Money In',
    format: 'currency',
    description: 'Total monthly income including all sources',
    chartType: 'area',
  },
  {
    key: 'money_leftover',
    label: 'Money leftover',
    format: 'currency',
    description: 'Remaining money after expenses and debt payments',
    chartType: 'area',
  },
  {
    key: 'verascore',
    label: 'VeraScore',
    format: 'number',
    description: 'Overall financial health score combining all factors',
    chartType: 'area',
    referenceLines: [
      {
        y: 80,
        label: 'Good',
        color: '#10B981',
      },
      {
        y: 60,
        label: 'Fair',
        color: '#F59E0B',
      },
    ],
  },
  {
    key: 'credit_score',
    label: 'Credit Score',
    format: 'number',
    description: 'Estimated credit score based on financial health',
    chartType: 'composed',
    referenceLines: [
      {
        y: 740,
        label: 'Excellent',
        color: '#10B981',
      },
      {
        y: 670,
        label: 'Good',
        color: '#0EA5E9',
      },
      {
        y: 580,
        label: 'Fair',
        color: '#F59E0B',
      },
    ],
  },
  {
    key: 'dti_ratio',
    label: 'Debt-to-Income Ratio',
    format: 'percentage',
    description: 'Monthly debt payments divided by monthly income',
    chartType: 'area',
    referenceLines: [
      {
        y: 36,
        label: 'Good DTI',
        color: '#10B981',
      },
      {
        y: 43,
        label: 'Fair DTI',
        color: '#F59E0B',
      },
    ],
  },
  {
    key: 'monthly_expenses',
    label: 'Monthly Expenses',
    format: 'currency',
    description: 'Average monthly spending',
    chartType: 'area',
  },
  {
    key: 'net_savings',
    label: 'Net Savings',
    format: 'currency',
    description: 'Money saved after expenses',
    chartType: 'area',
  },
  {
    key: 'total_outstanding_adverse_debt',
    label: 'Outstanding Debt',
    format: 'currency',
    description: 'Total debt balance',
    chartType: 'area',
  },
  {
    key: 'number_of_late_payments',
    label: 'Late Payments',
    format: 'number',
    description: 'Number of late payments in last 12 months',
    chartType: 'bar',
  },
]; 